import React, { useState, useEffect } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

interface Currency {
  code: string;
  symbol: string;
  name: string;
}

interface CurrencyBudgetProps {
  value: string;
  onChange: (value: string) => void;
  error?: string;
  label?: string;
  id?: string;
  className?: string;
  required?: boolean;
}

const currencies: Currency[] = [
  { code: "INR", symbol: "₹", name: "Indian Rupee" },
  { code: "USD", symbol: "$", name: "US Dollar" },
];

export const CurrencyBudgetInput: React.FC<CurrencyBudgetProps> = ({
  value,
  onChange,
  error,
  label = "Budget",
  id = "budget",
  className,
  required = false,
}) => {
  const [amount, setAmount] = useState("");
  const [selectedCurrency, setSelectedCurrency] = useState<Currency>(
    currencies[0]
  );
  const [open, setOpen] = useState(false);
  const [inputError, setInputError] = useState<string | undefined>(error);

  useEffect(() => {
    if (value) {
      for (const currency of currencies) {
        if (value.startsWith(currency.symbol)) {
          setSelectedCurrency(currency);
          setAmount(value.substring(currency.symbol.length).trim());
          return;
        }
      }
      setAmount(value);
    }
  }, [value]);

  // Handle amount changes
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    // Allow only numbers, commas, and decimal points
    const regex = /^[0-9,]*\.?[0-9]*$/;
    if (newValue === "" || regex.test(newValue)) {
      setAmount(newValue);
      updateValue(newValue, selectedCurrency);

      if (required && !newValue) {
        setInputError("Budget is required");
      } else {
        setInputError(undefined);
      }
    }
  };

  // Handle currency selection
  const selectCurrency = (currency: Currency) => {
    setSelectedCurrency(currency);
    updateValue(amount, currency);
    setOpen(false);
  };

  // Update the combined value
  const updateValue = (amt: string, currency: Currency) => {
    onChange(`${currency.symbol}${amt}`);
  };

  // Update error state when external error changes
  useEffect(() => {
    setInputError(error);
  }, [error]);

  return (
    <div className={cn("space-y-2", className)}>
      {label && (
        <Label htmlFor={id}>
          {label}
          {required && <span className='ml-1 text-destructive'>*</span>}
        </Label>
      )}
      <div className='flex'>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant='outline'
              role='combobox'
              aria-expanded={open}
              className='flex w-auto min-w-[80px] items-center gap-2 rounded-r-none border-r-0'
              type='button'
            >
              <span className='text-base'>{selectedCurrency.symbol}</span>
              <span className='text-sm'>{selectedCurrency.code}</span>
              <ChevronDown className='h-4 w-4 opacity-50' />
            </Button>
          </PopoverTrigger>
          <PopoverContent className='w-[200px] p-0'>
            <Command>
              <CommandList>
                <CommandEmpty>No currency found.</CommandEmpty>
                <CommandGroup>
                  {currencies.map((currency) => (
                    <CommandItem
                      key={currency.code}
                      value={currency.code}
                      onSelect={() => selectCurrency(currency)}
                      className='flex items-center gap-2'
                    >
                      <span className='text-base'>{currency.symbol}</span>
                      <span>{currency.code}</span>
                      <span className='ml-1 text-sm text-muted-foreground'>
                        {currency.name}
                      </span>
                      {currency.code === selectedCurrency.code && (
                        <Check className='ml-auto h-4 w-4' />
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <Input
          id={id}
          type='text'
          className='flex-1 rounded-l-none'
          placeholder='Enter amount (e.g., 20,000)'
          value={amount}
          onChange={handleAmountChange}
          required={required}
        />
      </div>
      {inputError && <p className='text-sm text-destructive'>{inputError}</p>}
    </div>
  );
};
